import { createUserWithEmailAndPassword } from "@firebase/auth";
import {
  UserCredential,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import React, { FC, useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Link,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import {
  captureLegacyEvent,
  capturePageView,
  captureProductEvent,
  markPendingEmailVerification,
  productAnalyticsEvents,
} from "../../analytics/events";
import LoadingScreen from "../../components/LoadingScreen";
import SaveButton from "../../components/SaveButton";
import { ViewIcon } from "../../components/icons";
import TextField from "../../components/styled/TextField";
import { firebaseAuth } from "../../configs/firebase";
import { useSettings } from "../../hooks/use-settings";
import useThirdPartyLogin from "../../hooks/use-third-party-login";
import GithubIcon from "../../icons/githubIcon";
import GoogleIcon from "../../icons/googleIcon";
import { parseErrorCode } from "../../utils/firebaseErrorParser";
import { getBaseUrl } from "../../utils/urls";
import { validateEmail } from "../../validators/email";
import LoginShell from "./LoginShell";

const CreateAccount: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const settings = useSettings();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [user, loading] = useAuthState(firebaseAuth);
  const [pageError, setPageError] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const {
    handleGoogleLogin,
    handleGithubLogin,
    isLoading: thirdPartyLoginLoading,
    error: thirdPartyLoginError,
  } = useThirdPartyLogin();

  const verifyEmailIfUnverified = async (
    userCredential: UserCredential | undefined,
  ) => {
    if (userCredential?.user.emailVerified === false) {
      markPendingEmailVerification(userCredential.user.uid);
      await sendEmailVerification(userCredential.user, {
        url: `${getBaseUrl()}/login`,
      });
    }
  };

  const shouldSave = (): boolean => {
    return (
      !!email && validateEmail(email) && !!password && password == password2
    );
  };

  const handleSignUp = async () => {
    setSaving(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );
      await updateProfile(userCredential.user, {
        displayName: fullName,
      });
      await verifyEmailIfUnverified(userCredential);
      captureProductEvent(productAnalyticsEvents.authSignupCompleted, {
        auth_provider: "password",
      });
      captureLegacyEvent("clicked_create_account", "auth", undefined, {
        accountType: "email and password",
      });
      if (import.meta.env.PROD) {
        //syft
        window?.syft?.signup(email || "", {
          firstName: fullName || "",
          type: "Email Sign-up",
        });
        //commonroom
        window?.signals?.identify({
          email: email || "",
          name: fullName || "",
        });
      }
    } catch (e) {
      setPageError(parseErrorCode(e));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setPageError(parseErrorCode(thirdPartyLoginError));
  }, [thirdPartyLoginError]);

  useEffect(() => {
    capturePageView(location.pathname, location.search, location.hash);
  }, [location.hash, location.pathname, location.search]);

  if (saving || loading || user || thirdPartyLoginLoading) {
    return <LoadingScreen />;
  }

  document.title = "Create Account";
  return (
    <LoginShell>
      <Card sx={{ boxShadow: 5 }}>
        <CardContent>
          <form>
            <Stack spacing={2} sx={{ mt: 5, px: 5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h3">Register</Typography>
                <Link
                  sx={{ cursor: "pointer" }}
                  color="primary"
                  onClick={() => navigate("/login")}
                  underline="none"
                  variant="subtitle2"
                >
                  Sign in
                </Link>
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Full Name</Typography>
                <TextField
                  value={fullName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFullName(e.target.value)
                  }
                />
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Email</Typography>
                <TextField
                  value={email}
                  error={!validateEmail(email) && !!email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                />
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Password</Typography>
                <TextField
                  value={password}
                  error={password !== password2}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ViewIcon
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Re-type password</Typography>
                <TextField
                  value={password2}
                  error={password !== password2}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword2(e.target.value)
                  }
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ViewIcon
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              {!!pageError && (
                <Stack direction="row" justifyContent="center">
                  <Alert severity="error" sx={{ maxWidth: "300px" }}>
                    {pageError}
                  </Alert>
                </Stack>
              )}
              <Stack direction="row" justifyContent="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ maxWidth: "300px" }}>
                  By clicking the buttons below, you acknowledge that you have
                  read, understood, and agree to Pomerium's{" "}
                  <Link
                    sx={{ cursor: "pointer" }}
                    color="primary"
                    underline="none"
                    variant="subtitle2"
                    href="https://www.pomerium.com/zero-terms-and-conditions/"
                    target="_blank"
                  >
                    terms of service
                  </Link>{" "}
                  and{" "}
                  <Link
                    sx={{ cursor: "pointer" }}
                    color="primary"
                    underline="none"
                    variant="subtitle2"
                    href="https://www.pomerium.com/privacy-policy"
                    target="_blank"
                  >
                    privacy policy
                  </Link>
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="center"
                sx={{ pt: 2 }}
                spacing={1}
              >
                <SaveButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: 300 }}
                  onClick={handleSignUp}
                  disabled={!shouldSave()}
                >
                  Create Account
                </SaveButton>
              </Stack>
            </Stack>
          </form>
          <Stack spacing={2} sx={{ mt: 2, px: 5 }}>
            <Stack direction="row" justifyContent="center" spacing={1}>
              <IconButton size="small" onClick={handleGoogleLogin}>
                <GoogleIcon />
              </IconButton>
              <IconButton size="small" onClick={handleGithubLogin}>
                <GithubIcon
                  fill={settings.paletteMode === "dark" ? "#c5d1de" : "#000"}
                />
              </IconButton>
            </Stack>
          </Stack>
          <Box mt={5} />
        </CardContent>
      </Card>
    </LoginShell>
  );
};

export default CreateAccount;
